import { useState, useCallback, useEffect } from 'react'
import { ContentState, EditorState, convertToRaw } from 'draft-js'
import { Editor, SyntheticEvent } from 'react-draft-wysiwyg'
import htmlToDraft from 'html-to-draftjs'
import draftToHtml from 'draftjs-to-html'
import './react-draft-wysiwyg.css'
import './RichEditor.css'
import { Stack, Typography, useTheme } from '@mui/material'
import classNames from 'classnames'

const getInitialState = (defaultValue: string) => {
  if (defaultValue) {
    const blocksFromHtml = htmlToDraft(defaultValue)
    const { contentBlocks, entityMap } = blocksFromHtml
    const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap)
    return EditorState.createWithContent(contentState)
  } else {
    return EditorState.createEmpty()
  }
}

export const ReadRichText = ({
  disableMaxHeight,
  value,
  label,
  isIndented,
  isOnLightBackground,
}: {
  disableMaxHeight?: boolean
  value: string
  label?: string
  isIndented?: boolean
  isOnLightBackground?: boolean
}) => {
  if (!value) {
    return null
  }
  return (
    <RichEditor
      isIndented={isIndented}
      disableMaxHeight={disableMaxHeight}
      label={label}
      placeholder=""
      defaultValue={value}
      onChange={() => {}}
      readOnly={true}
      isOnLightBackground={isOnLightBackground}
    />
  )
}

const RichEditor = ({
  defaultValue,
  disableMaxHeight,
  onChange,
  label,
  placeholder,
  readOnly,
  onBlur,
  subtitle,
  isIndented,
  isOnLightBackground,
}: {
  label?: string
  defaultValue: string
  disableMaxHeight?: boolean
  onChange: (html: string) => void
  placeholder: string
  readOnly?: boolean
  onBlur?: (event: SyntheticEvent) => void
  subtitle?: string
  isIndented?: boolean
  isOnLightBackground?: boolean
}) => {
  const theme = useTheme()
  const [editorState, setEditorState] = useState<any>(getInitialState(defaultValue))
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    setEditorState(getInitialState(defaultValue))
  }, [defaultValue, editorState])

  const onEditorStateChange = useCallback(
    (editorState: EditorState) => {
      setEditorState(editorState)
      if (!editorState.getCurrentContent().hasText()) {
        return onChange('')
      }
      return onChange(draftToHtml(convertToRaw(editorState.getCurrentContent())))
    },
    [onChange]
  )

  return (
    <Stack
      width="100%"
      sx={{
        '.DraftEditor-root': {
          maxHeight: disableMaxHeight ? '60vh' : 'auto',
        },
      }}>
      {(label || subtitle) && (
        <Stack mb="8px">
          {label && (
            <Typography variant="pXS" color="lightGrey">
              {label}
            </Typography>
          )}
          {subtitle && (
            <Typography mt="4px" variant="pS" color="lightGrey">
              {subtitle}
            </Typography>
          )}
        </Stack>
      )}

      <Editor
        onBlur={(e) => {
          onBlur?.(e)
          setIsFocused(false)
        }}
        onFocus={() => setIsFocused(true)}
        toolbarClassName="rich-text__toolbar"
        readOnly={readOnly}
        editorState={editorState}
        stripPastedStyles={true}
        onEditorStateChange={onEditorStateChange}
        placeholder={!isFocused ? placeholder : ''}
        wrapperClassName={classNames('demo-wrapper', {
          'read-only': readOnly,
          'is-indented': isIndented,
          'is-dark-mode': theme.palette.mode === 'dark',
          'is-on-light-background': isOnLightBackground,
        })}
        editorClassName="demo-editor"
        toolbarOnFocus={true}
        toolbar={{
          options: ['inline', 'blockType', 'list', 'link', 'emoji'],
          blockType: {
            options: ['Normal', 'H3', 'H4', 'Blockquote'],
          },
        }}
      />
    </Stack>
  )
}

RichEditor.defaultProps = {
  readOnly: false,
}

export default RichEditor
